import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

export interface FocusItem {
  image: string;
  tag: string;
  title: string;
  desc: string;
  to: string;
}

export default function HeroCarousel({ items }: { items: FocusItem[] }) {
  const [index, setIndex] = useState(0);

  const next = useCallback(() => setIndex((i) => (i + 1) % items.length), [items.length]);
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + items.length) % items.length),
    [items.length],
  );

  useEffect(() => {
    const timer = window.setInterval(next, 5200);
    return () => window.clearInterval(timer);
  }, [next]);

  const active = items[index];

  return (
    <div className="relative overflow-hidden rounded-lg border border-background-200 bg-secondary-900">
      <div className="relative h-[320px] w-full md:h-[420px]">
        {items.map((item, i) => (
          <img
            key={item.title}
            src={item.image}
            alt={item.title}
            title={item.title}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/40 to-foreground-950/10" />

        <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-8">
          <span className="w-fit rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
            {active.tag}
          </span>
          <h3 className="mt-3 max-w-2xl font-heading text-xl font-bold text-background-50 md:text-3xl">
            {active.title}
          </h3>
          <p className="mt-2 max-w-2xl line-clamp-2 text-sm leading-relaxed text-background-200 md:text-base">
            {active.desc}
          </p>
          <Link
            to={active.to}
            className="mt-4 w-fit rounded-md bg-accent-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
          >
            查看详情
            <i className="ri-arrow-right-line ml-1" />
          </Link>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={prev}
          className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-primary-500 md:left-5 md:h-10 md:w-10 cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={next}
          className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-primary-500 md:right-5 md:h-10 md:w-10 cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      <div className="absolute bottom-4 right-5 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.title}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              i === index ? "w-6 bg-primary-400" : "w-2 bg-background-50/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
}